import { useEffect, useState } from 'react'
import { scrollState } from './scrollState'

function smoothstep(x, a, b) {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)))
  return t * t * (3 - 2 * t)
}

// Doc: 8 beat sections × 100vh + 150vh runway = 950vh. Max scroll = 850vh.
// Section peaks (flex-end content) approx (t = scroll/850):
//   Beat 1  peak t ≈ 0.05     Stakes
//   Beat 2  peak t ≈ 0.16     Year-round
//   Beat 3  peak t ≈ 0.28     Purifiers fail
//   Connector peak (center) t ≈ 0.35   aqi0 air purifier
//   Beat 4.1 peak t ≈ 0.52
//   Beat 4.2 peak t ≈ 0.63
//   Beat 4.3 peak t ≈ 0.75
//   Beat 5  peak t ≈ 0.87
//   EndCard appears t > 0.94 (after Beat 5 fully scrolled out)
const LAYERS = [
  // All-WebP — ~6.6× smaller than the prior JPG set (~480 KB total vs ~3.2 MB).
  // Combined with the eager prefetch in the effect below + HTML <link rel=preload>,
  // every layer's image is in cache by the time the user scrolls into its range —
  // no dark gaps between scenes.
  { src: import.meta.env.BASE_URL + 'bg-beat1.webp?v=1', range: [-0.05, 0.12] },
  { src: import.meta.env.BASE_URL + 'bg-beat2.webp?v=2', range: [0.10, 0.24] },
  { src: import.meta.env.BASE_URL + 'bg-beat3.webp?v=2', range: [0.22, 0.36] },
  { src: import.meta.env.BASE_URL + 'bg-connector.webp?v=2', range: [0.30, 0.45] },
  // Beat 4 uses split-panel below (outdoor smog left + warm home right) wrapped around the 3D unit
  // Beat 5 image fades in as Beat 4 fades out, then stays through EndCard (dimmed)
  { src: import.meta.env.BASE_URL + 'bg-beat5.webp?v=2', range: [0.72, 1.10] },
]

// Beat 4 split-panel backdrop. Outdoor (smoggy) on the left half, indoor (warm
// home) on the right half — the 3D unit straddles the seam as the transformation
// point. Color filter shifts across the scroll range so smog "settles" while
// indoor light "warms up" as the journey progresses.
// Filenames (drop into /public/ once Gemini outputs are ready):
//   bg-beat4-outside.jpg  — outdoor smog (vertical 9:16 composition recommended)
//   bg-beat4-inside.jpg   — warm sunlit home interior (vertical 9:16)
const BEAT4_RANGE = [0.43, 0.78]
const BEAT4_OUTSIDE_SRC = import.meta.env.BASE_URL + 'bg-beat4-outside.webp'
const BEAT4_INSIDE_SRC = import.meta.env.BASE_URL + 'bg-beat4-inside.webp'
// Flip to true once the Gemini-generated split-panel images are in /public/.
// Until then the 3D unit renders against the wash gradient as it did before.
const BEAT4_IMAGES_READY = true

export default function Backgrounds({ isMobile }) {
  const [, force] = useState(0)

  // Eager prefetch — fire HTTP requests for ALL 7 backgrounds immediately on
  // mount so the browser has them in disk cache by the time the user scrolls
  // into each layer's range. Without this, React doesn't insert each layer's
  // <div> (and thus doesn't fetch its background-image) until scroll opacity
  // > 0.01 — producing a dark gap during each scene transition. Total weight
  // is ~480 KB across 7 WebPs; fetches run in parallel so the user can start
  // reading Beat 1 while the rest are still in flight.
  useEffect(() => {
    const all = [
      ...LAYERS.map(l => l.src),
      BEAT4_OUTSIDE_SRC,
      BEAT4_INSIDE_SRC,
    ]
    all.forEach((src) => {
      const img = new Image()
      img.src = src
    })
  }, [])

  useEffect(() => {
    let raf
    const loop = () => {
      force((n) => (n + 1) & 0xff)
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [])

  const t = scrollState.offset

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      {/* Base wash — subtle gradient, always visible */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, #1a1c20 0%, #2a2925 60%, #f7f5f1 100%)',
        }}
      />
      {LAYERS.map((layer, i) => {
        const [a, b] = layer.range
        const fadeIn = smoothstep(t, a, a + 0.05)
        const fadeOut = 1 - smoothstep(t, b - 0.05, b)
        let op = Math.min(fadeIn, fadeOut)
        // Beat 5 layer (baby) dims further once the EndCard is centred — index is 4 now
        // after inserting the connector layer.
        if (i === 4) op *= 1 - 0.45 * smoothstep(t, 0.94, 0.99)
        if (op <= 0.01) return null
        // On mobile (portrait 9:16) the wide source images get scaled to fit
        // viewport HEIGHT, then cropped horizontally. Subjects that sit left of
        // centre in the wide composition end up cut on the left edge. Shift
        // backgroundPosition X (not Y — vertical fits exactly, Y has no effect).
        let bgPos = 'center center'
        if (isMobile) {
          if (i === 0) bgPos = '30% center'  // India Gate at ~33% from left
          if (i === 4) bgPos = '35% center'  // Baby head + shoulders into centre
        }
        // Connector image (2048×2048 square, unit + logo in the top half). On
        // wide-but-short desktop windows, centre-centre crop cuts the logo top.
        // Bias the crop upward so the unit + logo stay in frame.
        if (i === 3) bgPos = '50% 18%'
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${layer.src})`,
              backgroundSize: 'cover',
              backgroundPosition: bgPos,
              opacity: op,
              filter: 'saturate(0.92) brightness(0.85)',
            }}
          />
        )
      })}
      {/* Beat 4 split-panel — outdoor smog (left) + warm home (right). Sits in
          front of the scenic layers but behind the scrim so 3D unit + copy stay
          on top. Color filters shift across the Beat 4 scroll range. Only
          renders once the Gemini-generated images exist on disk — until then
          the 3D unit shows against the wash gradient as before. */}
      {(() => {
        const [a, b] = BEAT4_RANGE
        const fadeIn = smoothstep(t, a - 0.03, a + 0.05)
        const fadeOut = 1 - smoothstep(t, b - 0.05, b + 0.03)
        const op = Math.min(fadeIn, fadeOut)
        if (op <= 0.01) return null
        // Skip until image assets exist (placeholder fallback would otherwise
        // black out the viewport during Beat 4).
        if (!BEAT4_IMAGES_READY) return null
        const localT = Math.max(0, Math.min(1, (t - a) / (b - a)))
        // Outdoor: stays cool/grey throughout, slight density shift
        const outdoorSat = 0.55 + 0.10 * smoothstep(localT, 0, 1)
        const outdoorBri = 0.72 - 0.04 * smoothstep(localT, 0, 1)
        // Indoor: warms and brightens as journey progresses (the destination)
        const indoorSat = 0.70 + 0.25 * smoothstep(localT, 0, 1)
        const indoorBri = 0.78 + 0.17 * smoothstep(localT, 0, 1)
        return (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', opacity: op }}>
            <div
              style={{
                flex: '1 1 50%',
                backgroundColor: '#1a1c20',
                backgroundImage: `url(${BEAT4_OUTSIDE_SRC})`,
                backgroundSize: 'cover',
                backgroundPosition: 'right center',
                filter: `saturate(${outdoorSat}) brightness(${outdoorBri})`,
              }}
            />
            <div
              style={{
                flex: '1 1 50%',
                backgroundColor: '#2a2620',
                backgroundImage: `url(${BEAT4_INSIDE_SRC})`,
                backgroundSize: 'cover',
                backgroundPosition: 'left center',
                filter: `saturate(${indoorSat}) brightness(${indoorBri})`,
              }}
            />
          </div>
        )
      })()}
      {/* Scrim — radial weight in the bottom-right arc + a soft vertical wash, so
          copy on the left and EndCard on the right both stay readable, without a
          hard horizontal "floor". */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse 95% 70% at 100% 100%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 45%, rgba(0,0,0,0) 75%), ' +
            'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.22) 65%, rgba(0,0,0,0.35) 100%)',
        }}
      />
    </div>
  )
}
